// src/pages/billing/schallan/CreateSChallan.jsx
import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  TextField,
  MenuItem,
  Button,
  Box,
  Grid,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Paper,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import api from "../../../utils/api";
import BillingLayout from "../../../layouts/BillingLayout";

const BillingCreateSChallan = () => {
  const billingUser = JSON.parse(localStorage.getItem("user"));
  const superAdminId = billingUser?.superAdminId;
  const navigate = useNavigate();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [units, setUnits] = useState([]);

  // Credit & customer state
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [creditBalance, setCreditBalance] = useState(0);
  const [netBalance, setNetBalance] = useState(0);
  const [creditLimitExceeded, setCreditLimitExceeded] = useState(false);

  const [form, setForm] = useState({
    customer: "",
    challanNumber: "",
    date: new Date().toISOString().slice(0, 10),
    items: [
      {
        product: "",
        unit: "",
        availableQty: 0,
        secondaryQuantity: 0,
        quantity: 0,
        price: 0,
        productDetails: null,
      },
    ],
  });

  const getTotal = () =>
    form.items.reduce((sum, it) => sum + it.quantity * it.price, 0);

  useEffect(() => {
    async function load() {
      const [cRes, pRes, uRes, cntRes] = await Promise.all([
        api.get(`/api/customers/get-all/${superAdminId}`),
        api.get(`/api/products/get-all/${superAdminId}`),
        api.get(`/api/units/get-all/${superAdminId}`),
        api.get(`/api/schallans/count/${superAdminId}`),
      ]);
      setCustomers(cRes.data);
      setUnits(uRes.data);
      setProducts(pRes.data);
      setForm((f) => ({
        ...f,
        challanNumber: String((cntRes.data.count || 0) + 1).padStart(3, "0"),
      }));
    }
    if (superAdminId) load();
  }, [superAdminId]);

  useEffect(() => {
    async function mergeStock() {
      const [pRes, stockRes] = await Promise.all([
        api.get(`/api/products/get-all/${superAdminId}`),
        api.get(`/api/reports/closing-stock/${superAdminId}`),
      ]);
      const stockMap = {};
      stockRes.data.forEach((s) => (stockMap[s.productName] = s.closingQty));
      setProducts(
        pRes.data.map((p) => ({
          ...p,
          closingQty: stockMap[p.productName] || 0,
        }))
      );
    }
    if (superAdminId) mergeStock();
  }, [superAdminId]);

  useEffect(() => {
    async function fetchCredit() {
      if (!selectedCustomer || selectedCustomer.paymentType !== "Credit") {
        setCreditBalance(0);
        setNetBalance(0);
        setCreditLimitExceeded(false);
        return;
      }
      const [salesRes, recRes] = await Promise.all([
        api.get(
          `/api/schallans/party/${superAdminId}/${selectedCustomer.partyName}`
        ),
        api.get(
          `/api/receipts/total/${superAdminId}/${selectedCustomer.partyName}`
        ),
      ]);
      const totalSales = salesRes.data.reduce((s, c) => s + (c.total || 0), 0);
      const totalRec = recRes.data.total || 0;
      const usedCredit = totalSales - totalRec;
      const remaining = selectedCustomer.creditBalance - usedCredit;
      setCreditBalance(selectedCustomer.creditBalance);
      setNetBalance(remaining);
      setCreditLimitExceeded(remaining <= 0);
    }
    fetchCredit();
  }, [selectedCustomer, superAdminId]);

  const handleItemChange = (idx, key, value) => {
    const items = [...form.items];
    const it = { ...items[idx] };

    if (key === "product") {
      const pd = products.find((p) => p.productName === value);
      if (pd) {
        it.product = pd.productName;
        it.unit = pd.primaryUnit;
        it.availableQty = pd.closingQty;
        it.price = pd.minSalePrice || 0;
        it.productDetails = pd;
        it.quantity = 0;
        it.secondaryQuantity = 0;
      }
    } else if (key === "quantity") {
      it.quantity = value;
      const sec = it.productDetails?.secondaryQuantity || 1;
      it.secondaryQuantity = parseFloat((value / sec).toFixed(2));
    } else if (key === "secondaryQuantity") {
      it.secondaryQuantity = value;
      const sec = it.productDetails?.secondaryQuantity || 1;
      it.quantity = parseFloat((value * sec).toFixed(2));
    } else if (key === "price") {
      it.price = value;
    }

    items[idx] = it;
    setForm({ ...form, items });
  };

  const addRow = () => {
    setForm((f) => ({
      ...f,
      items: [
        ...f.items,
        {
          product: "",
          unit: "",
          availableQty: 0,
          secondaryQuantity: 0,
          quantity: 0,
          price: 0,
          productDetails: null,
        },
      ],
    }));
  };

  const removeRow = (idx) => {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, i) => i !== idx),
    }));
  };

  const handleSubmit = async () => {
    if (selectedCustomer?.paymentType === "Credit" && creditLimitExceeded) {
      return alert("Credit limit exceeded");
    }
    if (selectedCustomer?.paymentType === "Credit" && getTotal() > netBalance) {
      return alert(`Net balance ₹${netBalance}, cannot exceed.`);
    }
    for (const it of form.items) {
      if (!it.productDetails?.minSalePrice) {
        return alert(`Min sale price missing for ${it.product}`);
      }
    }
    try {
      await api.post("/api/schallans/create", {
        ...form,
        superAdminId,
        total: getTotal(),
      });
      alert("Challan created");
      navigate("/billing/schallan/view");
    } catch {
      alert("Failed to create challan");
    }
  };

  return (
    <BillingLayout>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h5" align="center" gutterBottom>
          Create Sales Challan
        </Typography>

        {/* Header */}
        <Box mb={3}>
          {isMobile ? (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  select
                  label="Customer"
                  value={form.customer}
                  onChange={(e) => {
                    const cust = customers.find(
                      (c) => c.partyName === e.target.value
                    );
                    setSelectedCustomer(cust);
                    setForm({ ...form, customer: e.target.value });
                  }}
                  fullWidth
                >
                  {customers.map((c) => (
                    <MenuItem key={c._id} value={c.partyName}>
                      {c.partyName}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Challan No"
                  value={form.challanNumber}
                  onChange={(e) =>
                    setForm({ ...form, challanNumber: e.target.value })
                  }
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  type="date"
                  label="Date"
                  InputLabelProps={{ shrink: true }}
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  fullWidth
                />
              </Grid>
              {selectedCustomer?.paymentType === "Credit" && (
                <>
                  <Grid item xs={6}>
                    <TextField
                      label="Credit Bal"
                      value={creditBalance}
                      InputProps={{ readOnly: true }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Net Bal"
                      value={netBalance}
                      InputProps={{ readOnly: true }}
                      fullWidth
                    />
                  </Grid>
                </>
              )}
              {creditLimitExceeded && (
                <Grid item xs={12}>
                  <Typography color="error">
                    ⚠️ Credit limit exceeded
                  </Typography>
                </Grid>
              )}
            </Grid>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <TextField
                  select
                  label="Customer"
                  sx={{ width: 240 }}
                  value={form.customer}
                  onChange={(e) => {
                    const cust = customers.find(
                      (c) => c.partyName === e.target.value
                    );
                    setSelectedCustomer(cust);
                    setForm({ ...form, customer: e.target.value });
                  }}
                >
                  {customers.map((c) => (
                    <MenuItem key={c._id} value={c.partyName}>
                      {c.partyName}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Challan No"
                  sx={{ width: 240 }}
                  value={form.challanNumber}
                  onChange={(e) =>
                    setForm({ ...form, challanNumber: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  type="date"
                  label="Date"
                  sx={{ width: 240 }}
                  InputLabelProps={{ shrink: true }}
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                />
              </Grid>
              {selectedCustomer?.paymentType === "Credit" && (
                <>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label="Credit Bal"
                      sx={{ width: 240 }}
                      value={creditBalance}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label="Net Bal"
                      sx={{ width: 240 }}
                      value={netBalance}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                </>
              )}
              {creditLimitExceeded && (
                <Grid item xs={12}>
                  <Typography color="error">
                    ⚠️ Credit limit exceeded
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </Box>

        {/* Items */}
        {isMobile ? (
          form.items.map((it, idx) => (
            <Paper key={idx} variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <TextField
                    select
                    label="Product"
                    value={it.product}
                    size="small"
                    onChange={(e) =>
                      handleItemChange(idx, "product", e.target.value)
                    }
                    fullWidth
                  >
                    {products.map((p) => (
                      <MenuItem key={p._id} value={p.productName}>
                        {p.productName}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Unit"
                    value={it.unit}
                    size="small"
                    InputProps={{ readOnly: true }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Avail Stock"
                    value={it.availableQty}
                    size="small"
                    InputProps={{ readOnly: true }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Secondary Qty"
                    type="number"
                    value={it.secondaryQuantity}
                    size="small"
                    onChange={(e) =>
                      handleItemChange(
                        idx,
                        "secondaryQuantity",
                        Number(e.target.value)
                      )
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Primary Qty"
                    type="number"
                    value={it.quantity}
                    size="small"
                    onChange={(e) =>
                      handleItemChange(idx, "quantity", Number(e.target.value))
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Price"
                    type="number"
                    value={it.price}
                    size="small"
                    onChange={(e) =>
                      handleItemChange(idx, "price", Number(e.target.value))
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} container alignItems="center">
                  <Typography>
                    ₹{(it.quantity * it.price).toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={12} textAlign="right">
                  <IconButton onClick={() => removeRow(idx)}>
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </Paper>
          ))
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table sx={{ tableLayout: "fixed", width: "100%" }}>
              <TableBody>
                {form.items.map((it, idx) => (
                  <TableRow key={idx}>
                    <TableCell sx={{ width: "12.5%" }}>
                      <TextField
                        select
                        label="Product"
                        size="small"
                        sx={{ minWidth: 160 }}
                        value={it.product}
                        onChange={(e) =>
                          handleItemChange(idx, "product", e.target.value)
                        }
                        fullWidth
                      >
                        {products.map((p) => (
                          <MenuItem key={p._id} value={p.productName}>
                            {p.productName}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell sx={{ width: "12.5%" }}>
                      <TextField
                        label="Unit"
                        size="small"
                        value={it.unit}
                        InputProps={{ readOnly: true }}
                        fullWidth
                      />
                    </TableCell>
                    <TableCell sx={{ width: "12.5%" }}>
                      <TextField
                        label="Avail Stock"
                        size="small"
                        value={it.availableQty}
                        InputProps={{ readOnly: true }}
                        fullWidth
                      />
                    </TableCell>
                    <TableCell sx={{ width: "12.5%" }}>
                      <TextField
                        label="Secondary Qty"
                        type="number"
                        size="small"
                        value={it.secondaryQuantity}
                        onChange={(e) =>
                          handleItemChange(
                            idx,
                            "secondaryQuantity",
                            Number(e.target.value)
                          )
                        }
                        fullWidth
                      />
                    </TableCell>
                    <TableCell sx={{ width: "12.5%" }}>
                      <TextField
                        label="Primary Qty"
                        type="number"
                        size="small"
                        value={it.quantity}
                        onChange={(e) =>
                          handleItemChange(
                            idx,
                            "quantity",
                            Number(e.target.value)
                          )
                        }
                        fullWidth
                      />
                    </TableCell>
                    <TableCell sx={{ width: "12.5%" }}>
                      <TextField
                        label="Price"
                        type="number"
                        size="small"
                        value={it.price}
                        onChange={(e) =>
                          handleItemChange(idx, "price", Number(e.target.value))
                        }
                        fullWidth
                      />
                    </TableCell>
                    <TableCell sx={{ width: "12.5%" }}>
                      ₹{(it.quantity * it.price).toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ width: "12.5%" }}>
                      <IconButton onClick={() => removeRow(idx)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Footer */}
        <Box
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems="center"
          gap={2}
        >
          <Button variant="outlined" onClick={addRow} fullWidth={isMobile}>
            + Add Row
          </Button>
          <Typography variant="h6">Total: ₹{getTotal().toFixed(2)}</Typography>
        </Box>

        <Box textAlign="center" mt={3}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            size="large"
            fullWidth={isMobile}
          >
            Save Challan
          </Button>
        </Box>
      </Container>
    </BillingLayout>
  );
};

export default BillingCreateSChallan;
